import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { formatCompanyDateTime } from '../utils/companyDateFormat';
import { ArrowLeft, Loader2, Table2, X } from 'lucide-react';
import { SingleDayCalendarPicker } from '../components/SingleDayCalendarPicker';
import { useLocationContext } from '../components/Layout';
import { useWeekStartDay } from '../hooks/useWeekStartDay';
import { AttendanceImportRowsPreviewTable } from '../components/AttendanceImportRowsPreview';
import { attendanceApi } from '../services/api';
import type {
  AttendanceImportBatchDetail,
  AttendanceImportBatchSummary,
  AttendanceImportHistoryPage,
} from '../types';

const PATH = '/attendance/import-history';

function mergeSearch(
  current: URLSearchParams,
  updates: Record<string, string | number | undefined | null>
): string {
  const n = new URLSearchParams(current);
  for (const [key, val] of Object.entries(updates)) {
    if (val === undefined || val === null || val === '') n.delete(key);
    else n.set(key, String(val));
  }
  return n.toString();
}

const AttendanceImportHistory: React.FC = () => {
  const { selectedLocationId, locations } = useLocationContext();
  const weekStartsOn = useWeekStartDay();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Math.max(1, parseInt(searchParams.get('page') || '1', 10) || 1);
  const pageSize = Math.min(100, Math.max(1, parseInt(searchParams.get('pageSize') || '20', 10) || 20));
  const fromDate = searchParams.get('from') ?? '';
  const toDate = searchParams.get('to') ?? '';

  const [draftFrom, setDraftFrom] = useState(fromDate);
  const [draftTo, setDraftTo] = useState(toDate);
  const [filterError, setFilterError] = useState<string | null>(null);
  const [data, setData] = useState<AttendanceImportHistoryPage | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [viewingBatchId, setViewingBatchId] = useState<number | null>(null);
  const [viewingDetail, setViewingDetail] = useState<AttendanceImportBatchDetail | null>(null);
  const [viewingLoading, setViewingLoading] = useState(false);
  const [viewingError, setViewingError] = useState<string | null>(null);

  useEffect(() => {
    setDraftFrom(fromDate);
    setDraftTo(toDate);
  }, [fromDate, toDate]);

  useEffect(() => {
    if (!selectedLocationId) {
      setData(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    void attendanceApi
      .importHistory(selectedLocationId, {
        page,
        pageSize,
        fromDate: fromDate || undefined,
        toDate: toDate || undefined,
      })
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        if (!cancelled) {
          setLoadError('Could not load import history.');
          setData(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedLocationId, page, pageSize, fromDate, toDate]);

  const locationLabel = useMemo(() => {
    if (!selectedLocationId) return null;
    const loc = locations.find((l) => l.id === selectedLocationId);
    return loc?.name ?? `Location #${selectedLocationId}`;
  }, [selectedLocationId, locations]);

  const totalPages = useMemo(() => {
    if (!data) return 1;
    return Math.max(1, Math.ceil(data.totalCount / data.pageSize));
  }, [data]);

  const pageItems = useMemo(() => {
    const total = totalPages;
    if (total <= 20) return Array.from({ length: total }, (_, i) => i + 1);
    const set = new Set(
      [1, total, page, page - 1, page + 1, page - 2, page + 2].filter((p) => p >= 1 && p <= total)
    );
    return Array.from(set).sort((a, b) => a - b);
  }, [totalPages, page]);

  const handleSearch = () => {
    setFilterError(null);
    if (draftFrom && draftTo && draftFrom > draftTo) {
      setFilterError('From date must be on or before To date.');
      return;
    }
    const n = new URLSearchParams(searchParams);
    if (draftFrom) n.set('from', draftFrom);
    else n.delete('from');
    if (draftTo) n.set('to', draftTo);
    else n.delete('to');
    n.set('page', '1');
    setSearchParams(n);
  };

  const canResetFilters = Boolean(fromDate || toDate || draftFrom || draftTo);

  const handleResetFilters = () => {
    setFilterError(null);
    setDraftFrom('');
    setDraftTo('');
    const n = new URLSearchParams(searchParams);
    n.delete('from');
    n.delete('to');
    n.set('page', '1');
    setSearchParams(n);
  };

  const handleDownloadOriginal = useCallback(async (h: AttendanceImportBatchSummary) => {
    if (!h.fileDownloadUrl) return;
    try {
      const blob = await attendanceApi.importDownloadUploadedFile(h.id);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = h.fileName?.replace(/[\\/]/g, '_') || `import-batch-${h.id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      /* ignore */
    }
  }, []);

  const openViewBatchRows = useCallback(async (batchId: number) => {
    setViewingBatchId(batchId);
    setViewingDetail(null);
    setViewingError(null);
    setViewingLoading(true);
    try {
      const d = await attendanceApi.importBatchDetail(batchId);
      setViewingDetail(d);
    } catch {
      setViewingError('Could not load import details.');
    } finally {
      setViewingLoading(false);
    }
  }, []);

  const closeViewBatchRows = useCallback(() => {
    setViewingBatchId(null);
    setViewingDetail(null);
    setViewingError(null);
    setViewingLoading(false);
  }, []);

  const renderPageLinks = () => {
    const nodes: React.ReactNode[] = [];
    let prev = 0;
    for (const p of pageItems) {
      if (prev && p - prev > 1) {
        nodes.push(
          <span key={`gap-${p}`} className="px-1 text-gray-400 select-none">
            …
          </span>
        );
      }
      nodes.push(
        <Link
          key={p}
          to={{ pathname: PATH, search: mergeSearch(searchParams, { page: p }) }}
          className={`rounded px-2 py-1 text-sm ${
            p === page ? 'bg-blue-100 font-semibold text-blue-800' : 'text-blue-700 hover:underline'
          }`}
        >
          {p}
        </Link>
      );
      prev = p;
    }
    return nodes;
  };

  return (
    <div className="container mx-auto max-w-6xl p-4 sm:p-6">
      <div className="mb-6">
        <Link
          to="/attendance"
          className="mb-4 inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Attendance
        </Link>
        <h1 className="text-2xl font-bold text-gray-900">Import history</h1>
        <p className="mt-1 text-sm text-gray-600">
          Batches for the selected location, ordered by created time (UTC). Use the date filters to narrow by calendar day.
        </p>
      </div>

      {!selectedLocationId ? (
        <p className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
          Select a location in the header to view import history.
        </p>
      ) : (
        <>
          <p className="mb-4 text-sm text-gray-700">
            <span className="font-medium">Location:</span> {locationLabel}
          </p>

          <div className="mb-6 flex flex-wrap items-end gap-2 rounded-lg border border-gray-200 bg-gray-50 p-3">
            <div className="flex flex-col gap-0.5 text-xs font-medium text-gray-600">
              <span>From date</span>
              <SingleDayCalendarPicker
                value={draftFrom}
                onChange={setDraftFrom}
                weekStartsOn={weekStartsOn}
                max={draftTo || undefined}
                aria-label="Filter from date"
              />
            </div>
            <div className="flex flex-col gap-0.5 text-xs font-medium text-gray-600">
              <span>To date</span>
              <SingleDayCalendarPicker
                value={draftTo}
                onChange={setDraftTo}
                weekStartsOn={weekStartsOn}
                min={draftFrom || undefined}
                aria-label="Filter to date"
              />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSearch}
                disabled={loading}
                className="rounded bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Search
              </button>
              <button
                type="button"
                onClick={handleResetFilters}
                disabled={!canResetFilters || loading}
                className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:pointer-events-none disabled:opacity-40"
              >
                Reset
              </button>
            </div>
            {filterError ? <p className="w-full text-xs text-red-600">{filterError}</p> : null}
          </div>

          {loadError ? (
            <p className="text-sm text-red-700">{loadError}</p>
          ) : loading && !data ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Loading…
            </div>
          ) : data && data.items.length === 0 ? (
            <p className="text-sm text-gray-500">
              {fromDate || toDate ? 'No imports match these dates.' : 'No imports yet for this location.'}
            </p>
          ) : data ? (
            <>
              <div className="hidden md:block overflow-x-auto rounded-lg border border-gray-200 shadow-sm">
                <table className="min-w-full text-left text-sm text-gray-900">
                  <thead className="border-b border-gray-200 bg-gray-50 text-xs font-semibold uppercase tracking-wide text-gray-600">
                    <tr>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        ID
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        Status
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        Created (UTC)
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">
                        Rows
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">
                        Valid / Invalid
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5 text-right">
                        Imported / Failed
                      </th>
                      <th scope="col" className="min-w-[8rem] px-3 py-2.5">
                        File
                      </th>
                      <th scope="col" className="whitespace-nowrap px-3 py-2.5">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.items.map((h) => (
                      <tr key={h.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50/80">
                        <td className="whitespace-nowrap px-3 py-2.5 font-medium">#{h.id}</td>
                        <td className="whitespace-nowrap px-3 py-2.5">{h.status}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-gray-700">
                          {formatCompanyDateTime(h.createdAtUtc)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums">{h.totalRows}</td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-700">
                          {h.validRowCount} / {h.invalidRowCount}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5 text-right tabular-nums text-gray-700">
                          {h.status === 'Committed' ? `${h.importedCount} / ${h.failedCount}` : '—'}
                        </td>
                        <td className="max-w-[14rem] truncate px-3 py-2.5 text-gray-600" title={h.fileName ?? undefined}>
                          {h.fileName ?? '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2.5">
                          <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 text-blue-600 hover:underline"
                              onClick={() => void openViewBatchRows(h.id)}
                            >
                              <Table2 className="h-3.5 w-3.5 shrink-0" />
                              Preview
                            </button>
                            {h.fileDownloadUrl ? (
                              <button
                                type="button"
                                className="text-blue-600 hover:underline"
                                onClick={() => void handleDownloadOriginal(h)}
                              >
                                Download
                              </button>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <ul className="space-y-3 md:hidden" aria-label="Import batches">
                {data.items.map((h) => (
                  <li
                    key={h.id}
                    className="rounded-lg border border-gray-200 bg-white px-3 py-3 text-sm shadow-sm"
                  >
                    <div className="flex flex-wrap items-baseline justify-between gap-2">
                      <span className="font-semibold text-gray-900">#{h.id}</span>
                      <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700">
                        {h.status}
                      </span>
                    </div>
                    <p className="mt-1 text-xs text-gray-500">{formatCompanyDateTime(h.createdAtUtc)}</p>
                    <div className="mt-2 space-y-1 text-gray-700">
                      <p>
                        Rows: {h.totalRows} · Valid {h.validRowCount} / Invalid {h.invalidRowCount}
                      </p>
                      {h.status === 'Committed' ? (
                        <p>
                          Imported {h.importedCount} · Failed {h.failedCount}
                        </p>
                      ) : null}
                    </div>
                    {h.fileName ? (
                      <p className="mt-2 break-all text-xs text-gray-500">File: {h.fileName}</p>
                    ) : null}
                    <div className="mt-3 flex flex-wrap gap-x-4 gap-y-2 border-t border-gray-100 pt-3">
                      <button
                        type="button"
                        className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800"
                        onClick={() => void openViewBatchRows(h.id)}
                      >
                        <Table2 className="h-3.5 w-3.5" />
                        View row preview
                      </button>
                      {h.fileDownloadUrl ? (
                        <button
                          type="button"
                          className="text-sm font-medium text-blue-600 hover:text-blue-800"
                          onClick={() => void handleDownloadOriginal(h)}
                        >
                          Download original Excel
                        </button>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>

              <nav className="mt-6 flex flex-wrap items-center gap-2 border-t border-gray-200 pt-4" aria-label="Pagination">
                <Link
                  to={{ pathname: PATH, search: mergeSearch(searchParams, { page: page - 1 }) }}
                  className={`rounded border px-3 py-1.5 text-sm ${
                    page <= 1 ? 'pointer-events-none border-gray-200 text-gray-400' : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Previous
                </Link>
                <div className="flex flex-wrap items-center gap-1">{renderPageLinks()}</div>
                <Link
                  to={{ pathname: PATH, search: mergeSearch(searchParams, { page: page + 1 }) }}
                  className={`rounded border px-3 py-1.5 text-sm ${
                    page >= totalPages
                      ? 'pointer-events-none border-gray-200 text-gray-400'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  Next
                </Link>
                <span className="ml-auto text-xs text-gray-500">
                  Page {data.page} of {totalPages} · {data.totalCount} batch{data.totalCount === 1 ? '' : 'es'}
                </span>
              </nav>
            </>
          ) : null}
        </>
      )}

      {viewingBatchId != null && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="import-batch-rows-title"
        >
          <div className="flex max-h-[88vh] w-full max-w-4xl flex-col overflow-hidden rounded-lg bg-white shadow-xl">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h2 id="import-batch-rows-title" className="text-base font-semibold text-gray-900">
                Import batch #{viewingBatchId}
                {viewingDetail ? (
                  <span className="ml-2 font-normal text-gray-500">
                    · {viewingDetail.status} · {formatCompanyDateTime(viewingDetail.createdAtUtc)}
                  </span>
                ) : null}
              </h2>
              <button
                type="button"
                onClick={closeViewBatchRows}
                className="rounded p-1 text-gray-500 hover:bg-gray-100"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3">
              {viewingLoading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : viewingError ? (
                <p className="text-sm text-red-700">{viewingError}</p>
              ) : viewingDetail ? (
                <div className="space-y-3">
                  <p className="text-sm text-gray-600">
                    Rows: {viewingDetail.totalRows} · Valid {viewingDetail.validRowCount} · Invalid{' '}
                    {viewingDetail.invalidRowCount}
                    {viewingDetail.status === 'Committed' && (
                      <>
                        {' '}
                        · Imported {viewingDetail.importedCount} · Failed {viewingDetail.failedCount}
                      </>
                    )}
                  </p>
                  <AttendanceImportRowsPreviewTable rows={viewingDetail.rows} />
                </div>
              ) : null}
            </div>
            <div className="border-t px-4 py-2">
              <button
                type="button"
                onClick={closeViewBatchRows}
                className="rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceImportHistory;
